import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Upload,
  message,
  Image,
  Popconfirm,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import adminApiService from '../../services/adminApi';

interface Product {
  id: string;
  name: string;
  name_en: string;
  description: string;
  description_en: string;
  image: string;
  category: string;
  features: string[];
  specifications: Record<string, string>;
  sortOrder: number;
}

const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [form] = Form.useForm();
  const [uploadedImagePath, setUploadedImagePath] = useState<string>('');

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await adminApiService.getData('products');
      setProducts(response.data.products || []);
    } catch (error) {
      message.error('加载产品数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingProduct(null);
    form.resetFields();
    setUploadedImagePath('');
    setModalVisible(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    form.setFieldsValue({
      ...product,
      features: product.features?.join('\n') || '',
      specifications: JSON.stringify(product.specifications || {}, null, 2),
    });
    setUploadedImagePath(product.image);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const newProducts = products.filter((p) => p.id !== id);
      await adminApiService.updateData('products', { products: newProducts });
      message.success('删除成功');
      loadProducts();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const newProduct: Product = {
        id: editingProduct?.id || String(Date.now()),
        name: values.name,
        name_en: values.name_en,
        description: values.description,
        description_en: values.description_en,
        image: uploadedImagePath || values.image,
        category: values.category,
        features: values.features
          ? values.features.split('\n').filter((f: string) => f.trim())
          : [],
        specifications: values.specifications
          ? JSON.parse(values.specifications)
          : {},
        sortOrder: values.sortOrder,
      };

      let newProducts: Product[];
      if (editingProduct) {
        newProducts = products.map((p) =>
          p.id === editingProduct.id ? newProduct : p
        );
      } else {
        newProducts = [...products, newProduct];
      }

      await adminApiService.updateData('products', { products: newProducts });
      message.success(editingProduct ? '更新成功' : '添加成功');
      setModalVisible(false);
      loadProducts();
    } catch (error: any) {
      message.error(error.message || '保存失败');
    }
  };

  const handleUpload = async (file: File) => {
    try {
      const response = await adminApiService.uploadFile(file, 'images', 'products');
      setUploadedImagePath(response.path);
      message.success('图片上传成功');
      return false;
    } catch (error) {
      message.error('图片上传失败');
      return false;
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '图片',
      dataIndex: 'image',
      key: 'image',
      width: 120,
      render: (image: string) => (
        <Image src={image} alt="product" className="image-preview" />
      ),
    },
    {
      title: '产品名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '英文名称',
      dataIndex: 'name_en',
      key: 'name_en',
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: '特点数量',
      dataIndex: 'features',
      key: 'features',
      width: 100,
      render: (features: string[]) => features?.length || 0,
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: Product) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div>
      <h2 className="page-header">产品管理</h2>
      <div className="action-buttons">
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加产品
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={products}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingProduct ? '编辑产品' : '添加产品'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={700}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="产品名称（中文）"
            name="name"
            rules={[{ required: true, message: '请输入产品名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="产品名称（英文）"
            name="name_en"
            rules={[{ required: true, message: '请输入英文名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="描述（中文）"
            name="description"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            label="描述（英文）"
            name="description_en"
            rules={[{ required: true, message: '请输入英文描述' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="图片路径" name="image">
            <Input
              placeholder="图片路径或上传图片"
              value={uploadedImagePath}
              onChange={(e) => setUploadedImagePath(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="上传图片">
            <Upload
              beforeUpload={handleUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>点击上传</Button>
            </Upload>
            {uploadedImagePath && (
              <div style={{ marginTop: 8 }}>
                <Image src={uploadedImagePath} alt="preview" width={200} />
              </div>
            )}
          </Form.Item>
          <Form.Item
            label="分类"
            name="category"
            rules={[{ required: true, message: '请输入分类' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="特点（每行一个）"
            name="features"
          >
            <Input.TextArea rows={4} placeholder="每行输入一个特点" />
          </Form.Item>
          <Form.Item
            label="规格参数（JSON 格式）"
            name="specifications"
          >
            <Input.TextArea
              rows={6}
              placeholder='{"参数名": "参数值", ...}'
            />
          </Form.Item>
          <Form.Item
            label="排序"
            name="sortOrder"
            rules={[{ required: true, message: '请输入排序' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductsPage;
